KROWN.themeName = "Highlight";
KROWN.themeVersion = "4.0.0";

document.documentElement.style.setProperty(
  "--grid-perfect-width",
  document.documentElement.clientWidth + "px",
);
window.addEventListener("resize", () => {
  document.documentElement.style.setProperty(
    "--grid-perfect-width",
    document.documentElement.clientWidth + "px",
  );
});

const LITE_YOUTUBE_SELECTOR = ".lite-youtube, .lite-youtube-dev, lite-youtube";
const LITE_YOUTUBE_RETRY_LIMIT = 2;
const LITE_YOUTUBE_LOAD_TIMEOUT = 10000;

function liteYoutubeElements(root) {
  const elements = [];
  if (root?.matches?.(LITE_YOUTUBE_SELECTOR)) elements.push(root);
  root?.querySelectorAll?.(LITE_YOUTUBE_SELECTOR).forEach((element) => elements.push(element));
  return elements;
}

function loadLiteYoutubeScript(attempt = 0) {
  if (window.LiteYTEmbedDiv?.init) return Promise.resolve();
  if (window.liteYoutubeLoaderPromise) return window.liteYoutubeLoaderPromise;

  const script = document.createElement("script");
  const scriptUrl = new URL(KROWN.settings.routes.lite_youtube_script, document.baseURI);
  if (attempt) scriptUrl.searchParams.set("lite_youtube_retry", String(attempt));
  script.src = scriptUrl.href;
  script.defer = true;
  script.fetchPriority = "low";
  script.dataset.liteYoutubeLoader = "true";

  const request = new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer);
      script.removeEventListener("load", onLoad);
      script.removeEventListener("error", onError);
    };
    const onLoad = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error(`Failed to load ${script.src}`));
    };
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error(`Timed out loading ${script.src}`));
    }, LITE_YOUTUBE_LOAD_TIMEOUT);

    script.addEventListener("load", onLoad);
    script.addEventListener("error", onError);
  }).then(() => {
    if (!window.LiteYTEmbedDiv?.init) {
      throw new Error(`Loaded ${script.src} without initializing LiteYTEmbedDiv`);
    }
  });

  const promise = request.catch((error) => {
    script.remove();
    if (attempt >= LITE_YOUTUBE_RETRY_LIMIT) {
      if (window.liteYoutubeLoaderPromise === promise) window.liteYoutubeLoaderPromise = null;
      throw error;
    }

    return new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1))).then(() => {
      if (window.liteYoutubeLoaderPromise === promise) window.liteYoutubeLoaderPromise = null;
      return loadLiteYoutubeScript(attempt + 1);
    });
  });

  window.liteYoutubeLoaderPromise = promise;
  document.head.appendChild(script);
  return promise;
}

function loadLiteYoutube(root = document) {
  if (!liteYoutubeElements(root).length) return Promise.resolve();

  if (window.LiteYTEmbedDiv?.init) {
    window.LiteYTEmbedDiv.init(root);
    return Promise.resolve();
  }

  return loadLiteYoutubeScript().then(() => window.LiteYTEmbedDiv.init(root));
}

function observeLiteYoutube() {
  if (window.liteYoutubeObserver || !document.documentElement) return;

  window.liteYoutubeObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE || !liteYoutubeElements(node).length) return;
        loadLiteYoutube(node).catch((error) => console.error(error));
      });
    });
  });
  window.liteYoutubeObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

function secureExternalLinks(root = document) {
  const links = [];
  if (root?.matches?.("a[href]")) links.push(root);
  root?.querySelectorAll?.("a[href]").forEach((link) => links.push(link));
  const normalizeHost = (host) => host.replace(/^www\./, "");
  const currentHost = normalizeHost(window.location.hostname);

  links.forEach((link) => {
    const href = link.getAttribute("href");
    if (
      !href ||
      href.startsWith("#") ||
      href.startsWith("mailto:") ||
      href.startsWith("tel:") ||
      href.startsWith("javascript:")
    ) {
      return;
    }

    let url;
    try {
      url = new URL(href, window.location.href);
    } catch (error) {
      return;
    }

    if (normalizeHost(url.hostname) !== currentHost) {
      link.target = "_blank";
      link.relList.add("noopener");
    }
  });
}

const executeOnceOnDomContentLoaded = () => {
  // tab navigation

  document
    .querySelectorAll(
      ".css-slider, .product-variant__input, .simple-arrow, .mount-header [tabindex]",
    )
    .forEach((elm) => {
      elm.addEventListener("blur", (e) => {
        e.currentTarget.classList.remove("focus");
      });
    });

  let activeElement = document.activeElement;
  document.addEventListener("keyup", (e) => {
    if (e.keyCode == window.KEYCODES.TAB) {
      if (
        activeElement.classList.contains("focus") &&
        e.target != activeElement
      ) {
        activeElement.classList.remove("focus");
      }
      if (
        e.target.classList.contains("css-slider") ||
        e.target.classList.contains("product-variant__input") ||
        e.target.classList.contains("simple-arrow") ||
        e.target.getAttribute("tabindex") != "-1"
      ) {
        e.target.classList.add("focus");
      }
      activeElement = document.activeElement;
    }
  });

  // newsletter has jump

  if (window.location.pathname.includes("/challenge")) {
    setTimeout(() => {
      window.scroll({
        top: 0,
        behavior: "auto",
      });
    }, 300);
  }

  // fix image link borders

  document.querySelectorAll(".rte a img").forEach((elm) => {
    elm.parentNode.style.border = "none";
  });

  loadLiteYoutube().catch(() => {});
  observeLiteYoutube();

  // image banner scroll button

  document.querySelectorAll("[data-image-banner-scroll]").forEach((button) => {
    button.addEventListener("click", () => {
      const bannerSection = button.closest(".shopify-section");
      const nextSection = bannerSection?.nextElementSibling;

      if (nextSection) {
        const headerHeight = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--header-height",
          ),
        );
        const scrollTop = Math.max(
          0,
          nextSection.getBoundingClientRect().top +
            window.scrollY -
            (Number.isFinite(headerHeight) ? headerHeight : 0),
        );

        window.scrollTo({ top: scrollTop, behavior: "smooth" });
      }
    });
  });

  secureExternalLinks();
  if (!window.externalLinkObserver) {
    window.externalLinkObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) secureExternalLinks(node);
        });
      });
    });
    window.externalLinkObserver.observe(document.body, { childList: true, subtree: true });
  }
};

if (document.readyState !== "loading") {
  executeOnceOnDomContentLoaded();
} else {
  document.addEventListener("DOMContentLoaded", executeOnceOnDomContentLoaded);
}

// method for apps to tap into and refresh the cart

if (!window.refreshCart) {
  window.refreshCart = (openDrawer = true) => {
    return fetch("?section_id=helper-cart")
      .then((response) => response.text())
      .then((text) => {
        const sectionInnerHTML = new DOMParser().parseFromString(
          text,
          "text/html",
        );
        const sourceCartForm = sectionInnerHTML.getElementById("AjaxCartForm");
        const currentCartForm = document.getElementById("AjaxCartForm");
        const sourceCartSubtotal =
          sectionInnerHTML.getElementById("AjaxCartSubtotal");
        const currentCartSubtotal =
          document.getElementById("AjaxCartSubtotal");

        if (sourceCartForm && currentCartForm) {
          currentCartForm.innerHTML = sourceCartForm.innerHTML;
          currentCartForm.ajaxifyCartItems?.();
        }
        if (sourceCartSubtotal && currentCartSubtotal) {
          currentCartSubtotal.innerHTML = sourceCartSubtotal.innerHTML;
        }

        const cartCount = sourceCartForm?.querySelector("[data-cart-count]");
        const cartTotal =
          sourceCartSubtotal?.querySelector("[data-cart-total]");

        if (cartCount) {
          document
            .querySelectorAll("[data-header-cart-count]")
            .forEach((element) => {
              element.textContent = cartCount.textContent;
            });
        }
        if (cartTotal) {
          document
            .querySelectorAll("[data-header-cart-total]")
            .forEach((element) => {
              element.textContent = cartTotal.textContent;
            });
        }
        if (openDrawer) {
          document.getElementById("site-cart-sidebar")?.show?.();
        }
      });
  };
}

function setHeaderHeights() {
  const header = document.querySelector(".mount-header");
  const announcementBars = document.querySelectorAll(".mount-announcement-bar");
  const headerHeight = header?.getBoundingClientRect().height || 0;
  const announcementBarHeight = Array.from(announcementBars).reduce(
    (height, announcementBar) =>
      height + announcementBar.getBoundingClientRect().height,
    0,
  );

  document.documentElement.style.setProperty(
    "--main-header-height",
    `${headerHeight}px`,
  );
  document.documentElement.style.setProperty(
    "--announcement-bar-height",
    `${announcementBarHeight}px`,
  );
  document.documentElement.style.setProperty(
    "--header-height",
    `${headerHeight + announcementBarHeight}px`,
  );
}

const headerHeightObserver = new ResizeObserver(setHeaderHeights);
const headerHeightSelector = ".mount-header, .mount-announcement-bar";

document.querySelectorAll(headerHeightSelector).forEach((element) => {
  headerHeightObserver.observe(element);
});
setHeaderHeights();

document.addEventListener("shopify:section:load", (event) => {
  loadLiteYoutube(event.target).catch(() => {});
  if (!event.target.matches(headerHeightSelector)) return;
  headerHeightObserver.observe(event.target);
  setHeaderHeights();
});

document.addEventListener("shopify:section:unload", (event) => {
  if (!event.target.matches(headerHeightSelector)) return;
  headerHeightObserver.unobserve(event.target);
  requestAnimationFrame(setHeaderHeights);
});
